import React, { ReactNode, useEffect, useState } from "react";
import i18n from "i18next";
import {
  Button,
  Checkbox,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridProps,
  DataGridRow,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  TableColumnDefinition,
  TableRowId,
  Text,
} from "@fluentui/react-components";
import { StackItemShim, StackShim } from "@fluentui/react-migration-v8-v9";
import {
  ChevronDoubleLeftRegular,
  ChevronDoubleRightRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  MoreHorizontalRegular,
} from "@fluentui/react-icons";

interface ILuebPagedDataGridProps {
  customShowAll?: boolean;
  customPageCount?: number;
  customCurrentPage?: number;
  customTreffer?: number;
  itemsOnPage?: number; //Wenn nicht übergeben, dann 10 Items
  footer?: ReactNode;
  columns: TableColumnDefinition<any>[];
  items: any[];
  resizableColumns: boolean;
  columnSizingOptions: any;
  loading?: boolean;
  sortable?: boolean | undefined;
  //Es wird nur die ganze Liste sortiert, wenn man die columnId genau so wie die Attribute genannt hat.
  //Wenn man die columnId anders genannt hat, dann werden nur die angezeigten Items werden sortiert. ColumnId muss man dann auch z.B. "createdBy.fullName" nennen, wenn man das in Zelle rein schreibt.
  // COMPARE hinzufügen und dafür sorgen, dass die Werte nicht undefined/null sein können!!!!
  selectionMode?: string | undefined;
  selectedItems?: Set<TableRowId> | undefined;
  onSelectionChange?(
    e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
    data: any
  ): void;
  sortState?: any;
  onSortChange?(e: any, sortState: any): void;
  onPageChanged?(page: number): void;
}

function LuebPagedDataGrid(props: ILuebPagedDataGridProps) {
  // const getPageList = () => {
  //   return Array.from(Array(10).keys());
  // };
  const [maxItemsOnPage, setMaxItemsOnPage] = useState<number>(
    props.itemsOnPage ?? 10
  );
  const pageCount = props.customPageCount
    ? Math.ceil(props.customPageCount)
    : Math.ceil(props.items.length / maxItemsOnPage);
  const [currentPage, setCurrentPage] = useState<number>(
    props.customCurrentPage ?? 0
  );
  const [showAllItems, setShowAllItems] = useState<boolean>(false);
  const [itemsToShow, setItemsToShow] = useState<any>();
  useEffect(() => {
    setItemsToShow(props.items);
  }, [props.items]);

  // Hilfsfunktion, die basierend auf dem Typ einen vergleichbaren Wert zurückgibt
  const getComparableValue = (val: any): number | string => {
    // null oder undefined werden immer zum Ende sortiert
    if (val === null || val === undefined) return Infinity;

    // Zahlen werden unverändert übernommen
    if (typeof val === "number") return val;

    // Booleans werden zu 0 (false) bzw. 1 (true) umgewandelt
    if (typeof val === "boolean") return val ? 1 : 0;

    // Bei Strings prüfen wir, ob sie numerisch sind – falls ja, konvertieren wir sie in eine Zahl
    if (typeof val === "string") {
      const num = Number(val);
      return !isNaN(num) ? num : val;
    }

    // Datum als Zeitstempel (Millisekunden) ermitteln
    if (
      val instanceof Date ||
      Object.prototype.toString.call(val) === "[object Date]"
    ) {
      return val.getTime();
    }

    // Arrays werden anhand ihrer Länge verglichen
    if (Array.isArray(val)) return val.length;

    // Bei Objekten vergleichen wir anhand ihrer JSON-Repräsentation
    if (typeof val === "object") return JSON.stringify(val);

    // Letzter Fallback: in einen String umwandeln
    return String(val);
  };

  const sortObjects = <T extends Record<string, any>>(
    objects: T[],
    key: string,
    direction: "ascending" | "descending"
  ): T[] => {
    const getNestedValue = (obj: any, keyPath: string) =>
      keyPath
        .split(".")
        .reduce(
          (acc, part) => (acc !== undefined ? acc[part] : undefined),
          obj
        );

    return [...objects].sort((a, b) => {
      const valueA = getNestedValue(a, key);
      const valueB = getNestedValue(b, key);

      const compA = getComparableValue(valueA);
      const compB = getComparableValue(valueB);

      if (typeof compA === "number" && typeof compB === "number") {
        return direction === "ascending" ? compA - compB : compB - compA;
      } else {
        const result = String(compA).localeCompare(String(compB));
        return direction === "ascending" ? result : -result;
      }
    });
  };

  const onSortChange: DataGridProps["onSortChange"] = (e, nextSortState) => {
    if (props.sortable) {
      setItemsToShow(
        sortObjects(
          props.items,
          nextSortState.sortColumn as string,
          nextSortState.sortDirection
        )
      );
      if (props.onSortChange != undefined) {
        props.onSortChange(e, nextSortState);
      }
    }
  };

  useEffect(() => {
    if (currentPage > pageCount && props.customCurrentPage == undefined) {
      setCurrentPage(0);
    }
  }, [pageCount]);

  useEffect(() => {
    if (props.customShowAll) {
      setShowAllItems(props.customShowAll);
    }
  }, [props.customShowAll]);

  useEffect(() => {
    if (props.onPageChanged) {
      props.onPageChanged(currentPage);
    }
  }, [currentPage]);

  return (
    <>
      {itemsToShow && (
        <div>
          {props.items.length > (props.itemsOnPage ?? 10) &&
            showAllItems &&
            props.customShowAll == undefined && (
              <div>
                <Checkbox
                  checked={showAllItems}
                  label={"Alle anzeigen"}
                  onChange={(ev: any, data: any) => {
                    setShowAllItems(!showAllItems);
                  }}
                ></Checkbox>
                <Text className="lueb-marginRight10">
                  {props.customTreffer ?? props.items.length}{" "}
                  {i18n.t<string>("treffer")}
                </Text>
              </div>
            )}
          <DataGrid
            items={
              showAllItems || props.customCurrentPage != undefined
                ? itemsToShow
                : itemsToShow.slice(
                    currentPage * maxItemsOnPage,
                    (currentPage + 1) * maxItemsOnPage
                  )
            }
            columns={props.columns}
            columnSizingOptions={props.columnSizingOptions}
            resizableColumns={props.resizableColumns}
            sortable={props.sortable}
            selectionMode={props.selectionMode as any}
            selectedItems={props.selectedItems}
            onSelectionChange={props.onSelectionChange}
            sortState={props.sortState}
            onSortChange={onSortChange}
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<any>>
              {({ item, rowId }) => (
                <DataGridRow<any> key={rowId}>
                  {({ renderCell }) => (
                    <DataGridCell>{renderCell(item)}</DataGridCell>
                  )}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
          {(pageCount == 0 || currentPage + 1 == pageCount) && props.footer}
          {pageCount > 0 && (
            <div style={{ marginTop: 10 }}>
              <StackShim horizontal tokens={{ childrenGap: 10 }}>
                <div
                  style={{
                    display: "flex",
                  }}
                >
                  {pageCount > 1 && !showAllItems && (
                    <>
                      {pageCount > 5 && 0 != currentPage && (
                        <>
                          <Button
                            style={{ marginRight: "5px" }}
                            appearance="transparent"
                            icon={<ChevronDoubleLeftRegular />}
                            onClick={() => {
                              setCurrentPage(0);
                            }}
                          />
                          <Button
                            style={{ marginRight: "5px" }}
                            appearance="transparent"
                            icon={<ChevronLeftRegular />}
                            onClick={() => {
                              setCurrentPage(currentPage - 1);
                            }}
                          />
                        </>
                      )}
                      {Array.from(Array(pageCount).keys()).map((x, index) => {
                        if (
                          (currentPage < 3 && index < 4) ||
                          (currentPage > pageCount - 4 &&
                            index > pageCount - 6) ||
                          index == currentPage ||
                          index == currentPage + 1 ||
                          index == currentPage + 2 ||
                          index == currentPage - 1 ||
                          index == currentPage - 2
                        ) {
                          if (index == currentPage) {
                            return (
                              <Button
                                style={{
                                  marginRight: "5px",
                                  background: "#ff6600",
                                  color: "white",
                                  borderRadius: "100%",
                                  minWidth: "30px",
                                  height: "30px",
                                  maxWidth: "30px",
                                }}
                                size="small"
                                onClick={() => {
                                  {
                                    setCurrentPage(index);
                                  }
                                }}
                              >
                                {index + 1}
                              </Button>
                            );
                          } else {
                            return (
                              <Button
                                style={{
                                  marginRight: "5px",
                                  borderRadius: "100%",
                                  minWidth: "30px",
                                  height: "30px",
                                  maxWidth: "30px",
                                }}
                                size="small"
                                onClick={() => {
                                  {
                                    setCurrentPage(index);
                                  }
                                }}
                              >
                                {index + 1}
                              </Button>
                            );
                          }
                        }
                      })}
                      {pageCount > 5 && (
                        <>
                          <Menu>
                            <MenuTrigger>
                              <Button
                                style={{
                                  marginRight: "5px",
                                  borderRadius: "100%",
                                  minWidth: "30px",
                                  height: "30px",
                                  maxWidth: "30px",
                                }}
                                size="small"
                                icon={<MoreHorizontalRegular />}
                              />
                            </MenuTrigger>
                            <MenuPopover>
                              <MenuList>
                                {pageCount < 20 &&
                                  Array.from(Array(pageCount).keys()).map(
                                    (x, index) => {
                                      return (
                                        <MenuItem
                                          onClick={() => {
                                            {
                                              setCurrentPage(index);
                                            }
                                          }}
                                        >
                                          {index + 1}
                                        </MenuItem>
                                      );
                                    }
                                  )}
                                {pageCount > 20 &&
                                  Array.from(
                                    Array(Math.ceil(pageCount / 10)).keys()
                                  ).map((x) => {
                                    return (
                                      <Menu key={x}>
                                        <MenuTrigger disableButtonEnhancement>
                                          <MenuItem>
                                            {10 * x + 1}-{(x + 1) * 10}
                                          </MenuItem>
                                        </MenuTrigger>
                                        <MenuPopover>
                                          <MenuList>
                                            {Array.from(Array(10).keys()).map(
                                              (y, index) => {
                                                const pageNumber =
                                                  x * 10 + index + 1;
                                                return (
                                                  pageNumber <= pageCount && (
                                                    <MenuItem
                                                      key={pageNumber}
                                                      onClick={() => {
                                                        setCurrentPage(
                                                          x * 10 + index
                                                        );
                                                      }}
                                                    >
                                                      {pageNumber}
                                                    </MenuItem>
                                                  )
                                                );
                                              }
                                            )}
                                          </MenuList>
                                        </MenuPopover>
                                      </Menu>
                                    );
                                  })}
                              </MenuList>
                            </MenuPopover>
                          </Menu>
                          {pageCount - 1 != currentPage && (
                            <>
                              <Button
                                appearance="transparent"
                                icon={<ChevronRightRegular />}
                                onClick={() => {
                                  setCurrentPage(currentPage + 1);
                                }}
                              />
                              <Button
                                appearance="transparent"
                                icon={<ChevronDoubleRightRegular />}
                                onClick={() => {
                                  setCurrentPage(pageCount - 1);
                                }}
                              />
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>

                <StackItemShim align="center">
                  <Text className="lueb-marginRight10">
                    {props.customTreffer ?? props.items.length}{" "}
                    {i18n.t<string>("treffer")}
                  </Text>
                </StackItemShim>
                <StackItemShim align="center">
                  {props.items.length > (props.itemsOnPage ?? 10) &&
                    props.customShowAll == undefined && (
                      <Checkbox
                        checked={showAllItems}
                        label={"Alle anzeigen"}
                        onChange={(ev: any, data: any) => {
                          setShowAllItems(!showAllItems);
                        }}
                      ></Checkbox>
                    )}
                </StackItemShim>
              </StackShim>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default LuebPagedDataGrid;
